import type { Article, CategoryInfo, CategorySlug } from "@/types/news";
import { articles, categories } from "@/mocks/news";

export const allArticles = articles as Article[];
export const categoryList = categories as CategoryInfo[];
export const PAGE_SIZE = 3;

export const regionOptions = ["内地", "港台", "日韩", "欧美"];

export function getCategory(slug?: string): CategoryInfo | undefined {
  return categoryList.find((item) => item.slug === slug);
}

export function getArticleById(id?: string): Article | undefined {
  return allArticles.find((item) => item.id === id);
}

export function getFeatureArticles(): Article[] {
  return allArticles.filter((item) => item.featured);
}

export function getLatestArticles(limit?: number): Article[] {
  const sorted = [...allArticles].sort(
    (a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()
  );
  return typeof limit === "number" ? sorted.slice(0, limit) : sorted;
}

export function getHotArticles(limit?: number): Article[] {
  const sorted = [...allArticles].sort((a, b) => b.views - a.views);
  return typeof limit === "number" ? sorted.slice(0, limit) : sorted;
}

export function formatDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
}

export function formatViews(views: number): string {
  if (views >= 10000) return `${(views / 10000).toFixed(1)} 万`;
  return String(views);
}

export function getCategoryName(slug: CategorySlug): string {
  return getCategory(slug)?.name ?? "爆料";
}

export interface ArticleFilter {
  category?: string;
  keyword?: string;
  regions?: string[];
  sort?: "latest" | "hot";
}

export function filterArticles({
  category,
  keyword,
  regions = [],
  sort = "latest",
}: ArticleFilter): Article[] {
  const trimmed = keyword?.trim().toLowerCase() ?? "";

  const result = allArticles.filter((article) => {
    if (category && article.category !== category) return false;
    if (regions.length > 0 && !regions.includes(article.region)) return false;
    if (trimmed) {
      const haystack = [
        article.title,
        article.summary,
        article.author,
        article.region,
        getCategoryName(article.category),
        ...article.tags,
      ]
        .join(" ")
        .toLowerCase();
      if (!haystack.includes(trimmed)) return false;
    }
    return true;
  });

  return result.sort((a, b) => {
    if (sort === "hot") return b.views - a.views;
    return new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime();
  });
}

export function getRelatedArticles(article: Article, limit = 4): Article[] {
  const scored = allArticles
    .filter((item) => item.id !== article.id)
    .map((item) => {
      const sharedTags = item.tags.filter((tag) => article.tags.includes(tag)).length;
      const sameCategory = item.category === article.category ? 2 : 0;
      return { item, score: sharedTags + sameCategory };
    })
    .sort((a, b) => b.score - a.score || b.item.views - a.item.views);

  return scored.slice(0, limit).map((entry) => entry.item);
}